import { getSiteBom, type SiteBomRow } from '@api/vendor-portal';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Loader, Stack, Text } from '@mantine/core';
import { neutralColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';
import { formatCount } from '@utils/currency';

/**
 * The recipes this site works to.
 *
 * WHY A MANUFACTURER SEES THIS AT ALL. It is already working to a recipe — on paper, in
 * somebody's head, or in a spreadsheet the brand emailed over once. What it could not see was
 * the recipe the BRAND thinks it is working to, which is the one every number on both sides is
 * computed from: what the brand ships it, what a declaration consumes, what the plan buys. The
 * two disagreeing was invisible to both parties until a material ran out.
 *
 * ONLY ITS OWN PRODUCTS, AND ONLY ITS OWN RECIPE. The server scopes both — a site is never
 * shown a formulation for a SKU it does not make, and where it has a recipe of its own it sees
 * that one rather than the brand's default. A bill of materials is the most confidential thing
 * a brand has, and two of a brand's manufacturers must never be able to read each other's.
 *
 * READ-ONLY, with no edit affordance anywhere. The recipe is the brand's statement of how its
 * product is made; a site that believes it is wrong takes that up with the brand. An editable
 * copy here would mean the brand's plan and the site's consumption were computed from two
 * different documents, which is exactly the state this screen exists to end.
 */
/**
 * The code under a name — unless it IS the name.
 *
 * Troovy keys every material by its own name (`Seasoning_MIXVEG` is both), so the two lines
 * printed the same string twice on every row: not a label and its code, just noise the eye has
 * to read past eighteen times. Brands whose items carry a distinct SKU still get both lines,
 * which is the only reason the second line exists at all.
 */
function SubCode({ name, code }: { name: string; code: string | null }) {
    if (!code || code === name) return null;

    return (
        <Text size="11px" c="dimmed">
            {code}
        </Text>
    );
}

const VendorBomContainer: FC = () => {
    const [search, setSearch] = useState('');

    const { data, isLoading } = useQuery(['vendor-bom'], getSiteBom);

    // Memoised rather than defaulted inline: a fresh [] on every render would re-run every
    // derivation below it, on a screen whose whole job is to sit still and be read.
    const rows = useMemo(() => data?.rows ?? [], [data]);

    const filtered = useMemo(() => {
        if (!search) return rows;
        const needle = search.toLowerCase();
        return rows.filter(
            (row) =>
                row.productName.toLowerCase().includes(needle) ||
                row.productId.toLowerCase().includes(needle) ||
                row.materialName.toLowerCase().includes(needle) ||
                (row.materialSku ?? '').toLowerCase().includes(needle),
        );
    }, [rows, search]);

    const productCount = useMemo(
        () => new Set(rows.map((row) => row.productId)).size,
        [rows],
    );

    // How many of these lines are this site's own, rather than the brand's default. Worth
    // saying plainly: a site working to a recipe written for it should know that it is.
    const ownLineCount = useMemo(
        () => rows.filter((row) => row.vendorId).length,
        [rows],
    );

    const columns: DataSheetColumn<SiteBomRow>[] = useMemo(
        () => [
            {
                key: 'productName',
                header: 'Product',
                width: 240,
                sticky: true,
                render: (_: unknown, row: SiteBomRow) => (
                    // gap, not flush: the code under the name is a second fact about the row,
                    // not a continuation of it, and set tight the two read as one wrapped line.
                    <Stack gap={4}>
                        <Text size="13px" fw={500}>
                            {row.productName || row.productId}
                        </Text>
                        <SubCode
                            name={row.productName || row.productId}
                            code={row.productSku || row.productId}
                        />
                    </Stack>
                ),
            },
            {
                key: 'materialType',
                header: 'Type',
                width: 70,
                render: (value: string) => (
                    <Text size="11px" fw={600} c="dimmed">
                        {value}
                    </Text>
                ),
            },
            {
                key: 'materialName',
                header: 'Material',
                width: 260,
                render: (_: unknown, row: SiteBomRow) => (
                    <Stack gap={4}>
                        <Text size="13px">{row.materialName || row.materialId}</Text>
                        <SubCode
                            name={row.materialName || row.materialId}
                            code={row.materialSku}
                        />
                    </Stack>
                ),
            },
            {
                key: 'quantity',
                header: 'Per unit',
                width: 130,
                align: 'right' as const,
                render: (_: unknown, row: SiteBomRow) => (
                    <Text size="13px" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {/* Six places, matching what the brand stores: a seasoning line really
                            is 0.006325 kg, and rounding it here would disagree with the
                            consumption the same recipe produces. */}
                        {formatCount(row.quantity, { maxDecimals: 6 })}
                    </Text>
                ),
            },
            {
                key: 'unit',
                header: 'Unit',
                width: 80,
                render: (value: string) => (
                    <Text size="12px" c="dimmed">
                        {value || 'pcs'}
                    </Text>
                ),
            },
        ],
        [],
    );

    return (
        // The same shell every other vendor screen uses: `page-shell` is the standard page
        // gutter, and without it the title sat against the sidebar and the grid ran into the
        // right edge of the window.
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="📋"
                title="Bill of Materials"
                subtitle={
                    ownLineCount > 0
                        ? `What the brand says goes into each product you make. ${ownLineCount} of these lines were written for this site specifically.`
                        : 'What the brand says goes into each product you make.'
                }
            />

            {isLoading ? (
                <Loader size="sm" />
            ) : (
                <DataSheet
                    data={filtered}
                    columns={columns}
                    getRowKey={(row) => `${row.productId}::${row.materialId}`}
                    title="BOM Lines"
                    subtitle={`${productCount} product${productCount === 1 ? '' : 's'}, ${rows.length} line${rows.length === 1 ? '' : 's'}`}
                    enableSearch
                    searchPlaceholder="Search by product or material..."
                    searchValue={search}
                    onSearchChange={setSearch}
                    emptyState={
                        <Text size="13px" ta="center" p={32} c={neutralColors.textMuted}>
                            The brand has not recorded a bill of materials for the products you
                            make. Ask them to add one — it is what tells them what to ship you.
                        </Text>
                    }
                />
            )}
        </div>
    );
};

export default VendorBomContainer;
